// Angular Core
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Third Party Services
import { JwtHelperService } from '@auth0/angular-jwt';

// Interfaces
import { InterceptorError } from '../../interfaces/InterceptErrors';
import { TokenConfigs } from '../../interfaces/TokenConfigs';
import { TokenInfo, TokenRawInfo } from '../../interfaces/TokenInfo';

// Environment
import { environment } from '../../../environments/environment';

// RxJS
import { catchError, Observable, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private access_token!: TokenInfo;
  private jwtService: JwtHelperService = new JwtHelperService();
  private apiUrl: string = environment.apiUrl;
  refreshAttempts: number = 0;

  // Dependency Injections
  private httpClient: HttpClient = inject(HttpClient);
  private router: Router = inject(Router);

  incrementRefreshAttempts() {
    this.refreshAttempts++;
  }

  resetRefreshAttempts() {
    this.refreshAttempts = 0;
  }

  setToken(tokenRaw: TokenRawInfo): void {
    const {
      token,
      iss: tokenIssuer,
      iat: tokenIssuedAt,
      exp: tokenExpiration,
      nbf: tokenIssuedUntil,
      jti: tokenId,
      sub: tokenSubject,
      prv: tokenPrivateClaims,
      id: user_id,
      email: user_email,
      name: user_name,
    } = tokenRaw;

    let tokenInfo = {
      token,
      tokenIssuer,
      tokenIssuedAt,
      tokenIssuedUntil,
      tokenExpiration,
      tokenId,
      tokenSubject,
      tokenPrivateClaims,
      user: {
        id: user_id,
        email: user_email,
        name: user_name,
      },
    } as TokenInfo;

    this.access_token = tokenInfo;
  }

  storeTokenInSession(token: TokenConfigs) {
    if (!token.access_token.includes('Bearer')) {
      token.access_token = `Bearer ${token.access_token}`;
    }

    token.access_token = token.access_token;
    sessionStorage.setItem('access_token', JSON.stringify(token));
  }

  getToken(): TokenRawInfo {
    let tokenRaw = sessionStorage.getItem('access_token');
    if (!tokenRaw) {
      return {} as TokenRawInfo;
    }

    let token: TokenConfigs = JSON.parse(tokenRaw);

    let decodedToken = this.decodeToken(token.access_token);
    decodedToken.token = token.access_token;

    return decodedToken;
  }

  getRefreshToken(): TokenRawInfo {
    let tokenRaw = sessionStorage.getItem('access_token');
    if (!tokenRaw) {
      return {} as TokenRawInfo;
    }

    let token: TokenConfigs = JSON.parse(tokenRaw);

    let decodedToken = this.decodeToken(token.refresh_token);
    decodedToken.token = token.refresh_token;

    return decodedToken;
  }

  refreshToken(): Observable<TokenConfigs> {
    return this.httpClient
      .post<TokenConfigs>(`${this.apiUrl}/auth/refresh`, {})
      .pipe(
        tap((response: TokenConfigs) => {
          if (!response.access_token.includes('Bearer')) {
            response.access_token = `Bearer ${response.access_token}`;
          }

          let decodedToken = this.decodeToken(response.access_token);
          decodedToken.token = response.access_token;

          this.setToken(decodedToken);
          this.storeTokenInSession(response);
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError((): InterceptorError => {
            this.router.navigate(['/login']);
            return {
              errorType: 'refreshTokenError',
              error: {
                error: 'Sessão expirada, realize o login novamente.',
                code: 'I401',
              },
            };
          });
        })
      );
  }

  decodeToken(decodedToken: string): TokenRawInfo {
    return this.jwtService.decodeToken(decodedToken) as TokenRawInfo;
  }
}
